'use client';

/**
 * TransferRequest.tsx — «Be om flytting»-skjema i heroen på /domener/flytt
 *
 * Ber KUN om domenenavnet og lager en e-postforespørsel. Overføringskoden (EPP/auth-
 * kode) skal aldri sendes på e-post eller oppgis her; teksten sier at vi sender en
 * sikker lenke. Valideringen bruker parseDomainQuery (lib/domain.ts) og krever at
 * brukeren har skrevet en endelse.
 *
 * Tilgjengelighet: <form> med synlig etikett, advarselen koblet med aria-describedby,
 * aria-invalid + feilmelding, og et aria-live-område.
 */

import { useId, useState, type FormEvent } from 'react';
import { ArrowRight } from '@carbon/icons-react';
import { normalizeQuery, parseDomainQuery } from '@/lib/domain';
import type { DomainTransferPageContent } from '@/types/domain';
import styles from './domain.module.css';

type Result = { kind: 'idle' } | { kind: 'error'; message: string } | { kind: 'ready'; domain: string };

export default function TransferRequest({ request }: { request: DomainTransferPageContent['request'] }) {
  const uid = useId();
  const [value, setValue] = useState('');
  const [result, setResult] = useState<Result>({ kind: 'idle' });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const normalized = normalizeQuery(value);
    if (!normalized) {
      setResult({ kind: 'error', message: request.errors.empty });
      return;
    }
    // Endelsen er den siste delen etter punktum; flytting krever at den er skrevet
    const parts = normalized.split('.');
    const tld = parts.length > 1 ? parts[parts.length - 1] : '';
    const parsed = tld ? parseDomainQuery(normalized, [tld]) : null;
    if (!parsed || !parsed.ok || !parsed.tld) {
      setResult({ kind: 'error', message: request.errors.invalid });
      return;
    }
    setResult({ kind: 'ready', domain: `${parsed.name}.${parsed.tld}` });
  };

  return (
    <div className={styles.search}>
      <form aria-label={request.label} onSubmit={onSubmit} noValidate>
        <label htmlFor={`${uid}-d`} className={styles.label}>
          {request.label}
        </label>
        <div className={styles.field}>
          <input
            id={`${uid}-d`}
            type="text"
            inputMode="url"
            autoComplete="off"
            autoCapitalize="none"
            spellCheck={false}
            className={styles.input}
            placeholder={request.placeholder}
            value={value}
            aria-invalid={result.kind === 'error'}
            aria-describedby={`${uid}-warn ${uid}-status`}
            onChange={(e) => setValue(e.target.value)}
          />
          <button type="submit" className={styles.submit}>
            <span>{request.button}</span>
            <ArrowRight size={20} aria-hidden />
          </button>
        </div>
        <p id={`${uid}-warn`} className={styles.warning}>
          {request.warning}
        </p>
      </form>

      <div id={`${uid}-status`} aria-live="polite" className={styles.status}>
        {result.kind === 'error' && <p className={styles.error}>{result.message}</p>}
        {result.kind === 'ready' && (
          <div className={styles.results}>
            <ul className={styles.resultList}>
              <li className={styles.resultRow}>
                <span className={styles.resultName}>{result.domain}</span>
                <span />
                <a className={styles.resultAction} href={request.contactHref.replace('{domain}', encodeURIComponent(result.domain))}>
                  {request.button}
                  <ArrowRight size={16} aria-hidden />
                </a>
              </li>
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
